import React, { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Loader2, Lock } from 'lucide-react';

// Cache Stripe.js per publishable key for the page lifetime.
const stripePromiseCache = {};
const getStripePromise = (publishableKey) => {
  if (!publishableKey) return null;
  if (!stripePromiseCache[publishableKey]) {
    stripePromiseCache[publishableKey] = loadStripe(publishableKey);
  }
  return stripePromiseCache[publishableKey];
};

// Inner form, must be rendered inside <Elements>.
function CheckoutForm({ clientSecret, amount, recipientName, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();

  const [paymentRequest, setPaymentRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Build the Payment Request (Apple Pay / Google Pay). Stripe handles
  // the device detection and `canMakePayment` resolves only on supported
  // browsers — the button auto-hides everywhere else.
  useEffect(() => {
    if (!stripe || !amount || !clientSecret) return;
    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: `Tip for ${recipientName || 'service pro'}`,
        amount: Math.round(amount * 100),
      },
      requestPayerName: false,
      requestPayerEmail: false,
    });
    pr.canMakePayment().then((result) => {
      if (result) setPaymentRequest(pr);
    });

    pr.on('paymentmethod', async (ev) => {
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false },
      );

      if (confirmError) {
        ev.complete('fail');
        onError(confirmError.message || 'Payment failed');
        return;
      }

      ev.complete('success');

      if (paymentIntent && paymentIntent.status === 'requires_action') {
        const { error: actionError, paymentIntent: finalIntent } =
          await stripe.confirmCardPayment(clientSecret);
        if (actionError) {
          onError(actionError.message || 'Authentication failed');
          return;
        }
        onSuccess(finalIntent.id);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent.id);
      }
    });
  }, [stripe, clientSecret, amount, recipientName, onSuccess, onError]);

  const handleCardSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Stripe requires return_url for redirect-based methods (e.g. some
        // bank methods, 3DS). For pure card/Link/Apple Pay/Google Pay the
        // user typically does not leave the page.
        return_url: `${window.location.origin}/thanks`,
      },
      redirect: 'if_required',
    });

    if (error) {
      onError(error.message || 'Payment failed');
      setSubmitting(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      // For redirect flows Stripe handles navigation; nothing more to do.
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="tip-checkout">
      {paymentRequest && (
        <div data-testid="payment-request-button">
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
              style: {
                paymentRequestButton: {
                  type: 'default',
                  theme: 'dark',
                  height: '48px',
                },
              },
            }}
          />
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs uppercase tracking-widest text-[#52525B]">
              or pay another way
            </span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
        </div>
      )}

      <form onSubmit={handleCardSubmit} className="space-y-4">
        <PaymentElement
          options={{
            layout: 'tabs',
            // Link auto-appears when payment_method_types include link.
          }}
        />
        <button
          type="submit"
          disabled={!stripe || submitting}
          className="btn-primary flex items-center justify-center gap-2 w-full"
          data-testid="card-pay-button"
        >
          {submitting ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              Processing...
            </>
          ) : (
            <>
              <Lock size={16} />
              Pay ${Number(amount || 0).toFixed(2)}
            </>
          )}
        </button>
      </form>
    </div>
  );
}

// Public wrapper — supplies the Stripe.js Promise and Elements context.
export default function TipCheckout({
  clientSecret,
  publishableKey,
  amount,
  recipientName,
  onSuccess,
  onError,
}) {
  const stripePromise = useMemo(() => getStripePromise(publishableKey), [publishableKey]);

  if (!clientSecret || !publishableKey || !stripePromise) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="tip-checkout-loading">
        <Loader2 className="animate-spin text-[#F5C542]" size={28} />
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#F5C542',
            colorBackground: '#0a0a0a',
            colorText: '#ffffff',
            colorDanger: '#ef4444',
            fontFamily: 'Manrope, system-ui, sans-serif',
            borderRadius: '12px',
          },
        },
      }}
    >
      <CheckoutForm
        clientSecret={clientSecret}
        amount={amount}
        recipientName={recipientName}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
}
